/**
 * Admin API service – allowed emails & registered users management
 */
import { apiFetch } from '@/lib/api-client'

const ADMIN_BASE_URL = '/api/data/admin'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AllowedEmail {
  id: string
  email: string
  is_admin: boolean
  notes: string | null
  created_at: string
}

export interface RegisteredUser {
  id: string
  email: string
  full_name?: string | null
  created_at: string | null
  last_sign_in_at: string | null
  email_confirmed_at: string | null
}

export interface AddAllowedEmailPayload {
  email: string
  is_admin?: boolean
  notes?: string
}

export interface UpdateAllowedEmailPayload {
  is_admin?: boolean
  notes?: string | null
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

interface ApiResponse<T> {
  success: boolean
  data: T
}

export async function fetchAllowedEmails(): Promise<AllowedEmail[]> {
  const res = await apiFetch<ApiResponse<AllowedEmail[]>>('/allowed-emails', {
    baseUrl: ADMIN_BASE_URL,
  })
  return res.data
}

export async function fetchRegisteredUsers(): Promise<RegisteredUser[]> {
  const res = await apiFetch<ApiResponse<RegisteredUser[]>>('/registered-users', {
    baseUrl: ADMIN_BASE_URL,
  })
  return res.data
}

export async function addAllowedEmail(payload: AddAllowedEmailPayload): Promise<AllowedEmail> {
  const res = await apiFetch<ApiResponse<AllowedEmail>>('/allowed-emails', {
    baseUrl: ADMIN_BASE_URL,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  return res.data
}

export async function updateAllowedEmail(
  id: string,
  payload: UpdateAllowedEmailPayload,
): Promise<AllowedEmail> {
  const res = await apiFetch<ApiResponse<AllowedEmail>>(`/allowed-emails/${id}`, {
    baseUrl: ADMIN_BASE_URL,
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  return res.data
}

export async function fetchLastSeen(): Promise<Record<string, string>> {
  const res = await apiFetch<ApiResponse<Record<string, string>>>('/last-seen', {
    baseUrl: ADMIN_BASE_URL,
  })
  return res.data
}

export async function deleteAllowedEmail(id: string): Promise<{ deleted: boolean; email: string }> {
  const res = await apiFetch<ApiResponse<{ deleted: boolean; email: string }>>(
    `/allowed-emails/${id}`,
    {
      baseUrl: ADMIN_BASE_URL,
      method: 'DELETE',
    },
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Phase 06 SSO-01/02: Grafana tek-tık SSO (imzalı cookie issue endpoint)
// ---------------------------------------------------------------------------

export interface GrafanaSsoResponse {
  ok: boolean
  redirect?: string
}

/**
 * POST /admin/grafana-sso — admin Bearer JWT ile backend imzalı `gf_sso` cookie
 * bastırır (path=/grafana). Başarıda `redirect` (`/grafana/`) döner; düz navigasyon
 * nginx auth_request'ten geçer. Bearer JWT apiFetch tarafından otomatik eklenir.
 */
export async function issueGrafanaSso(): Promise<GrafanaSsoResponse> {
  return apiFetch<GrafanaSsoResponse>('/grafana-sso', {
    baseUrl: ADMIN_BASE_URL,
    method: 'POST',
  })
}

// ---------------------------------------------------------------------------
// #701 BLG-03: Blog erişim bileti (yalnız admin; blog herkese kapalıyken)
// ---------------------------------------------------------------------------

export interface BlogSsoResponse {
  ok: boolean
  redirect?: string
}

/**
 * POST /admin/blog-sso — admin Bearer JWT ile backend imzalı `blog_gate` çerezini
 * bastırır (Path=/blog); `/blog/*` istekleri bu çerezle kapıdan geçer. Bearer JWT
 * apiFetch tarafından otomatik eklenir. Yanıttaki `redirect` gezinme hedefi olarak
 * KULLANILMAZ: çağıran sabit `/blog/` adresine gider.
 */
export async function issueBlogSso(): Promise<BlogSsoResponse> {
  return apiFetch<BlogSsoResponse>('/blog-sso', {
    baseUrl: ADMIN_BASE_URL,
    method: 'POST',
  })
}

// ---------------------------------------------------------------------------
// Phase 02-06 SE-03: Kullanıcı Aktivitesi (aktif oturumlar + giriş geçmişi)
// ---------------------------------------------------------------------------

export interface ActiveSession {
  user_id: string
  session_id: string
  email?: string | null
  ip?: string | null
  user_agent?: string | null
  device?: string | null
  browser?: string | null
  os?: string | null
  login_at?: string | null
  last_seen?: string | null
}

export interface LoginHistoryEvent {
  id: number
  user_id: string
  email: string | null
  event_type: string
  session_id: string | null
  ip: string | null
  user_agent: string | null
  device: string | null
  result: string | null
  created_at: string | null
}

/** GET /admin/active-sessions — kim şu an online (Redis registry). */
export async function getActiveSessions(): Promise<{
  sessions: ActiveSession[]
  count: number
}> {
  const res = await apiFetch<ApiResponse<{ sessions: ActiveSession[]; count: number }>>(
    '/active-sessions',
    { baseUrl: ADMIN_BASE_URL },
  )
  return res.data
}

/** GET /admin/login-history — giriş geçmişi (audit, DESC). */
export async function getLoginHistory(params?: {
  limit?: number
  userId?: string
}): Promise<{ events: LoginHistoryEvent[]; count: number }> {
  const q = new URLSearchParams()
  if (params?.limit) q.set('limit', String(params.limit))
  if (params?.userId) q.set('user_id', params.userId)
  const qs = q.toString()
  const res = await apiFetch<ApiResponse<{ events: LoginHistoryEvent[]; count: number }>>(
    `/login-history${qs ? `?${qs}` : ''}`,
    { baseUrl: ADMIN_BASE_URL },
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Phase 64 D-12: Admin Amazon Sync API
// ---------------------------------------------------------------------------

export interface AdminSyncTypeStatus {
  last_completed: string | null
  last_records: number
  recent_runs: number
  recent_failed: number
  recent_running: number
  health: 'ok' | 'warning' | 'error' | 'running' | 'never'
}

export interface AdminFailedJob {
  id: string
  job_type: string
  error_message: string | null
  retry_count: number
  created_at: string | null
  completed_at: string | null
}

export interface AdminSyncStatusResponse {
  connected: boolean
  seller_name: string | null
  last_sync_at: string | null
  type_status: Record<string, AdminSyncTypeStatus>
  failed_jobs: AdminFailedJob[]
  total_jobs: number
  total_failed: number
}

export async function adminGetAmazonSyncStatus(
  supabaseUserId: string,
): Promise<AdminSyncStatusResponse> {
  const res = await apiFetch<ApiResponse<AdminSyncStatusResponse>>(
    `/amazon/admin/sync-status/${supabaseUserId}`,
    { baseUrl: '/api' },
  )
  return res.data
}

/**
 * Phase 64 D-12 + Phase 65.1 D-04..D-06:
 * Supported sync types: orders, settlement, financial_transactions, business_report,
 * fba_inventory, sales_traffic, fba_fees, listings, sqp, ads_campaigns,
 * reconciliation (Phase 65.1 — hesap eslestirme, delta),
 * reconciliation_backfill (Phase 65.1 — geri donuk eslestirme, admin manuel).
 */
export async function adminTriggerAmazonSync(
  supabaseUserId: string,
  syncType: string,
): Promise<{ status: string; task_id?: string; message: string }> {
  const res = await apiFetch<ApiResponse<{ status: string; task_id?: string; message: string }>>(
    `/amazon/admin/sync/${syncType}/${supabaseUserId}`,
    { baseUrl: '/api', method: 'POST' },
  )
  return res.data
}
